import * as React from 'react';
import { CaretDown, Check } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
type SearchSelectOption = {
  label: string;
  value: string;
  searchText?: string;
  disabled?: boolean;
  alwaysVisible?: boolean;
};
type SearchSelectProps = {
  options: SearchSelectOption[];
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  popoverClassName?: string;
  disabled?: boolean;
  modalPopover?: boolean;
  allowClear?: boolean;
  context?: 'default' | 'modal';
  size?: 'default' | 'large';
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
};
export function SearchSelect({
  options,
  value,
  defaultValue = null,
  onValueChange,
  onOpenChange,
  placeholder = 'Select option',
  searchPlaceholder = 'Search...',
  emptyText = 'No results found.',
  className,
  popoverClassName,
  disabled = false,
  modalPopover = false,
  allowClear = false,
  context = 'default',
  size = 'default',
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
}: SearchSelectProps) {
  const [open, setOpen] = React.useState(false);

  const [internalValue, setInternalValue] = React.useState<string | null>(
    defaultValue
  );

  const listboxId = React.useId();
  const valueTextId = React.useId();
  const selectedValueDescriptionId = React.useId();
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);

  const [portalContainer, setPortalContainer] =
    React.useState<HTMLElement | null>(null);

  const isModalContext = context === 'modal';
  const isControlled = value !== undefined;
  const selectedValue = isControlled ? (value ?? null) : internalValue;

  React.useEffect(() => {
    if (!triggerRef.current) {
      return;
    }

    const nearestDialog = triggerRef.current.closest(
      "[data-slot='modal'], [role='dialog'], [aria-modal='true']"
    );

    // Mount next to the dialog content (not inside it) so overflow clipping
    // from modal containers does not cut off the flyout.
    setPortalContainer((nearestDialog?.parentElement as HTMLElement) ?? null);
  }, []);

  const selectedOption = React.useMemo(
    () => options.find((option) => option.value === selectedValue) ?? null,
    [options, selectedValue]
  );

  const setSelectedValue = React.useCallback(
    (nextValue: string | null) => {
      if (!isControlled) {
        setInternalValue(nextValue);
      }
      if (nextValue !== null) {
        onValueChange?.(nextValue);
      }
    },
    [isControlled, onValueChange]
  );

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        onOpenChange?.(nextOpen);
      }}
      modal={modalPopover || Boolean(portalContainer)}
    >
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          id={id}
          type="button"
          role="combobox"
          aria-label={ariaLabelledby ? undefined : (ariaLabel ?? placeholder)}
          aria-labelledby={
            ariaLabelledby ? `${ariaLabelledby} ${valueTextId}` : undefined
          }
          aria-describedby={
            !ariaLabelledby && selectedOption
              ? selectedValueDescriptionId
              : undefined
          }
          aria-controls={listboxId}
          aria-expanded={open}
          data-context={context}
          className={cn(
            'appearance-none flex w-full items-center justify-between gap-2 rounded-md border border-border-subtle font-sans font-normal leading-[1.5] text-fg-primary [border-color:var(--input-border-color,var(--border-subtle))] [background-color:var(--input-bg,var(--surface-input-default))] [color:var(--input-fg,var(--text-primary))] transition-[border-color,box-shadow] duration-150 ease-in-out disabled:cursor-not-allowed disabled:opacity-60 focus-visible:[border-color:var(--input-focus-border,var(--color-ring-default))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:[box-shadow:inset_0_0_0_2px_var(--input-focus-ring,var(--color-ring-default))]',
            size === 'large'
              ? 'h-14 pl-3 pr-5 text-base'
              : 'h-12 pl-3 pr-4 text-base',
            isModalContext &&
              '[--input-border-color:var(--color-modal-border)] [--input-bg:var(--surface-tint-inverse)] [--input-fg:var(--color-modal-fg)] [--input-placeholder:var(--color-modal-fg)] [--input-focus-border:var(--color-modal-fg)] [--input-focus-ring:var(--color-modal-fg)] focus-visible:!ring-0',
            !selectedOption &&
              (isModalContext
                ? 'text-[var(--color-modal-fg)]/70'
                : 'text-fg-secondary'),
            className
          )}
          disabled={disabled}
        >
          <span id={valueTextId} className="truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          {selectedOption && !ariaLabelledby && (
            <span id={selectedValueDescriptionId} className="sr-only">
              Selected: {selectedOption.label}
            </span>
          )}
          <CaretDown
            aria-hidden="true"
            focusable="false"
            weight="fill"
            className="ml-2 h-2 w-2 shrink-0"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        id={listboxId}
        portalContainer={portalContainer}
        className={cn(
          'w-[var(--radix-popover-trigger-width)] p-0',
          isModalContext &&
            'border-[var(--color-modal-border)] bg-[var(--surface-tint-inverse)] text-[var(--color-modal-fg)] backdrop-blur-none',
          popoverClassName
        )}
      >
        <Command
          label={`Search ${ariaLabel ?? placeholder}`}
          className={cn(
            "[&_[data-slot='command-input-wrapper']]:h-12 [&_[data-slot='command-input']]:h-12 [&_[data-slot='command-input']]:pl-0 [&_[data-slot='command-input']]:pr-2",
            isModalContext &&
              "bg-transparent text-[var(--color-modal-fg)] [&_[data-slot='command-input-wrapper']]:border-[var(--color-modal-border)] [&_[data-slot='command-item'][data-selected=true]]:bg-[var(--color-neutral-0-10)]"
          )}
        >
          <CommandInput
            aria-label={`Search ${ariaLabel ?? placeholder}`}
            placeholder={searchPlaceholder}
            className="pl-0 pr-2"
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValue === option.value;
                return (
                  <CommandItem
                    key={option.value}
                    value={option.searchText ?? option.label}
                    forceMount={option.alwaysVisible}
                    disabled={option.disabled}
                    onSelect={() => {
                      if (option.disabled) {
                        return;
                      }
                      if (allowClear && isSelected) {
                        setSelectedValue(null);
                        setOpen(false);
                        return;
                      }
                      setSelectedValue(option.value);
                      setOpen(false);
                    }}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && (
                      <Check
                        aria-hidden="true"
                        focusable="false"
                        className="ml-auto h-4 w-4"
                      />
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
